/**
 * CategoryDetailModal - Shared component for viewing category transaction details
 *
 * Used by:
 * - CategoriesPage (full functionality with rename)
 * - OverviewPage (view-only mode)
 */

import { useState, useEffect, useMemo } from 'react';
import { Category, Transaction, Account } from '@services/db';
import { Modal, Button, Input } from '@/components/shared';
import {
  formatCurrency as formatCurrencyIntl,
  formatDate as formatDateIntl,
  formatMonthDay,
  formatMonthYear,
  formatMonthYearShort,
} from '@/utils/format';
import { useLanguage, categoryDisplayName, t as i18nT } from '@/i18n';
import { getCategoryIcon } from '@/utils/categoryIcons';
import './CategoryDetailModal.css';

// ============================================================
// Types
// ============================================================

export interface CategoryWithStats extends Category {
  amount: number;
  transactionCount: number;
  budget?: number;
  percentage?: number;
}

export interface CategoryDetailModalProps {
  category: CategoryWithStats | null;
  transactions: Transaction[];
  accounts: Account[];
  currentMonth: Date;
  onClose: () => void;
  onRename?: (id: string, newName: string) => Promise<void>;
  // Resolves with the service result so a blocked delete (category still
  // referenced by transactions/schedules) can be explained inline
  onDelete?: (id: string) => Promise<{ success: boolean; inUseCount?: number; scheduleCount?: number }>;
  onMonthChange: (direction: 'prev' | 'next') => void;
  showRename?: boolean;
  /** Optional date range - when provided, shows all transactions in range instead of month-by-month */
  dateRange?: { start: Date; end: Date };
}

// ============================================================
// Helpers
// ============================================================

// Central locale-aware formatters (utils/format.ts) - BUG-074 safe-currency
// fallback now lives there. Never inline Intl here.
function formatCurrency(amount: number, currency?: string): string {
  return formatCurrencyIntl(amount, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatMonth(date: Date): string {
  return formatMonthYear(date);
}

function formatDateRange(start: Date, end: Date): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

  if (sameMonth) {
    return formatMonthYear(startDate);
  }

  if (sameYear) {
    return `${formatDateIntl(startDate, { month: 'short' })} - ${formatMonthYearShort(endDate)}`;
  }

  return `${formatMonthYearShort(startDate)} - ${formatMonthYearShort(endDate)}`;
}

function formatDate(date: Date): string {
  return formatMonthDay(new Date(date));
}

function getProgressColor(percentage: number): string {
  if (percentage >= 100) return 'var(--expense)';
  if (percentage >= 80) return '#f0ad4e';
  return 'var(--income)';
}

// ============================================================
// Component
// ============================================================

export function CategoryDetailModal({
  category,
  transactions,
  accounts,
  currentMonth,
  onClose,
  onRename,
  onDelete,
  onMonthChange,
  showRename = true,
  dateRange,
}: CategoryDetailModalProps) {
  const { t, language } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete flow: confirm step, then inline blocked notice if refused
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState<{ txns?: number; sched?: number } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Reset edit state when category changes
  useEffect(() => {
    if (category) {
      setEditName(category.name);
      setIsEditing(false);
      setConfirmingDelete(false);
      setDeleteBlocked(null);
    }
  }, [category]);

  // Filter transactions for this category
  // If dateRange provided, filter by range; otherwise filter by month
  const categoryTransactions = useMemo(() => {
    if (!category) return [];

    return transactions
      .filter((t) => {
        if (t.categoryId !== category.id) return false;
        const d = new Date(t.date);
        
        if (dateRange) {
          // Filter by date range
          return d >= new Date(dateRange.start) && d <= new Date(dateRange.end);
        } else {
          // Filter by month
          const year = currentMonth.getFullYear();
          const month = currentMonth.getMonth();
          return d.getFullYear() === year && d.getMonth() === month;
        }
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map((t) => ({
        ...t,
        accountName: accounts.find((a) => a.id === t.accountId)?.name || i18nT('categories.detail.unknown_account'),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, category, currentMonth, accounts, dateRange, language]);

  // Calculate total
  const totalAmount = useMemo(() => {
    return categoryTransactions.reduce((sum, t) => sum + t.amount, 0);
  }, [categoryTransactions]);

  // Early return AFTER all hooks
  if (!category) return null;

  const handleSaveRename = async () => {
    if (!editName.trim() || editName === category.name || !onRename) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onRename(category.id!, editName.trim());
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const canRename = showRename && onRename;
  const canDelete = showRename && onDelete;

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      const result = await onDelete(category.id!);
      if (!result.success) {
        setConfirmingDelete(false);
        setDeleteBlocked({ txns: result.inUseCount, sched: result.scheduleCount });
      }
      // Success closes the modal from the parent handler
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal isOpen={!!category} onClose={onClose} title="" size="lg">
      <div className="category-detail-modal">
        {/* Category Header */}
        <div className="category-detail-header">
          <div
            className="category-detail-icon"
            style={{ background: category.color ? `${category.color}30` : 'var(--bg-surface)' }}
          >
            <span>{getCategoryIcon(category.name, category.icon)}</span>
          </div>

          {isEditing && canRename ? (
            <div className="category-edit-name">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t('categories.detail.name_placeholder')}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveRename();
                  if (e.key === 'Escape') setIsEditing(false);
                }}
              />
              <div className="category-edit-actions">
                <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={saving}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleSaveRename} disabled={saving}>
                  {saving ? t('common.saving') : t('common.save')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="category-detail-title">
              <h2>{categoryDisplayName(category.name)}</h2>
              {canRename && (
                <button className="rename-btn" onClick={() => setIsEditing(true)} title={t('categories.detail.rename')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              )}
            </div>
          )}

          <div className="category-detail-type">
            <span className={`type-pill ${category.type}`}>
              {category.type === 'expense' ? t('common.expense') : t('common.income')}
            </span>
          </div>
        </div>

        {/* Month/Period Navigator */}
        <div className="category-detail-month">
          {dateRange ? (
            // Date range mode - just show the period, no navigation
            <span className="month-text">{formatDateRange(dateRange.start, dateRange.end)}</span>
          ) : (
            // Month mode - show navigation buttons
            <>
              <button className="month-nav-btn" onClick={() => onMonthChange('prev')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <span className="month-text">{formatMonth(currentMonth)}</span>
              <button className="month-nav-btn" onClick={() => onMonthChange('next')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Summary Stats */}
        <div className="category-detail-stats">
          <div className="stat-item">
            <div className="stat-label">{t('categories.detail.transactions')}</div>
            <div className="stat-value">{categoryTransactions.length}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">{t('categories.detail.total')}</div>
            <div className={`stat-value ${category.type === 'expense' ? 'text-expense' : 'text-income'}`}>
              {category.type === 'expense' ? '-' : '+'}{formatCurrency(totalAmount)}
            </div>
          </div>
          {category.budget && (
            <div className="stat-item">
              <div className="stat-label">{t('categories.detail.budget')}</div>
              <div className="stat-value">{formatCurrency(category.budget)}</div>
            </div>
          )}
        </div>

        {/* Budget Progress - only show in month mode (budget is monthly) */}
        {!dateRange && category.budget && category.percentage !== undefined && (
          <div className="category-detail-budget">
            <div className="budget-label">
              <span>{t('categories.detail.budget_used', { percent: category.percentage })}</span>
              <span>{formatCurrency(totalAmount)} / {formatCurrency(category.budget)}</span>
            </div>
            <div className="budget-progress">
              <div
                className="budget-progress-fill"
                style={{
                  width: `${Math.min(100, category.percentage)}%`,
                  background: getProgressColor(category.percentage),
                }}
              />
            </div>
          </div>
        )}

        {/* Transaction List */}
        <div className="category-detail-transactions">
          <h3 className="transactions-title">{t('categories.detail.transactions')}</h3>

          {categoryTransactions.length === 0 ? (
            <div className="transactions-empty">
              <span className="empty-icon">📭</span>
              <p>{dateRange ? t('categories.detail.empty_period') : t('categories.detail.empty_month')}</p>
            </div>
          ) : (
            <div className="category-txn-list">
              {categoryTransactions.map((txn) => (
                <div key={txn.id} className="category-txn-row">
                  <div className="category-txn-info">
                    <div className="category-txn-description">{txn.description || t('transactions.upcoming.no_description')}</div>
                    <div className="category-txn-meta">
                      <span className="category-txn-date">{formatDate(txn.date)}</span>
                      <span className="category-txn-separator">·</span>
                      <span className="category-txn-account">{txn.accountName}</span>
                    </div>
                  </div>
                  <div className={`category-txn-amount ${category.type === 'expense' ? 'text-expense' : 'text-income'}`}>
                    {category.type === 'expense' ? '-' : '+'}{formatCurrency(txn.amount, txn.currency)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete (management mode only) - confirm inline, and explain
            inline when the service refuses because the category is in use */}
        {canDelete && (
          <div className="catdel-zone">
            {deleteBlocked ? (
              <div className="catdel-blocked">
                <p className="catdel-blocked-msg">
                  {deleteBlocked.sched
                    ? t('categories.detail.delete_blocked_sched', { count: deleteBlocked.sched })
                    : t('categories.detail.delete_blocked_txns', { count: deleteBlocked.txns ?? 0 })}
                </p>
                <Button variant="ghost" onClick={() => setDeleteBlocked(null)}>
                  {t('common.close')}
                </Button>
              </div>
            ) : confirmingDelete ? (
              <div className="catdel-confirm">
                <p className="catdel-confirm-msg">
                  {t('categories.detail.delete_message', { name: categoryDisplayName(category.name) })}
                </p>
                <div className="catdel-confirm-buttons">
                  <Button variant="ghost" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                    {deleting ? t('categories.detail.deleting') : t('categories.detail.yes_delete')}
                  </Button>
                </div>
              </div>
            ) : (
              <button className="catdel-trigger" onClick={() => setConfirmingDelete(true)}>
                {t('categories.detail.delete')}
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
